import React from "react";
import styles from "./activeCampaignsPanel.module.css";
import { Button } from "../../../../components/Buttons";

const stateKey = (seq) => {
  const s = (seq?.status || "").toLowerCase();
  if (s === "unenrolled") return "unenrolled";
  if (s === "active") return "running";
  if (s === "paused") return "paused";
  // A finished sequence used to fall through to "default", which rendered a grey card labelled
  // "Sequence" - so a contact whose sequence was over looked no different from one whose state we
  // could not read, and the Unenroll button stayed on it. The backend writes 'completed' on all
  // three endings now (all steps sent, reply received, unsubscribed).
  if (s === "completed" || s === "stopped" || s === "interrupted") return "completed";
  return "default";
};

const themeForState = (key) => {
  switch (key) {
    case "running":
      return {
        card: styles.cardRunning,
        dot: styles.dotRunning,
        label: styles.labelRunning,
      };
    case "paused":
      return {
        card: styles.cardPaused,
        dot: styles.dotPaused,
        label: styles.labelPaused,
      };
    case "completed":
      return {
        card: styles.cardCompleted,
        dot: styles.dotCompleted,
        label: styles.labelCompleted,
      };
    case "unenrolled":
      return {
        card: styles.cardUnenrolled,
        dot: styles.dotUnenrolled,
        label: styles.labelUnenrolled,
      };
    default:
      return {
        card: styles.cardDefault,
        dot: styles.dotDefault,
        label: styles.labelDefault,
      };
  }
};

const headerLabelForState = (key) => {
  if (key === "unenrolled") return "Unenrolled";
  if (key === "completed") return "Completed";
  if (key === "paused") return "Paused";
  if (key === "running") return "Active";
  return "Sequence";
};

const ActiveSequencePanel = ({ loading, sequences = [], onUnenroll }) => {
  if (loading) {
    return (
      <div className={styles.wrap}>
        <div className={`${styles.card} ${styles.cardDefault}`}>
          <div
            className={styles.skeleton}
            style={{ height: 11, width: 110 }}
          />
          <div
            className={styles.skeleton}
            style={{ height: 14, width: 160 }}
          />
          <div
            className={styles.skeleton}
            style={{ height: 12, width: 140 }}
          />
        </div>
      </div>
    );
  }

  if (!sequences?.length) return null;

  return (
    <div className={styles.wrap}>
      {sequences.map((s) => {
        const key = stateKey(s);
        const theme = themeForState(key);
        // Same rule as marketing campaigns, and the same list on purpose: anything terminal hides
        // the action, because there is nothing left to unenroll from and pressing it produced
        // nonsense on a sequence that had already ended. Paused does NOT hide it - a paused
        // sequence will resume, and unenrolling is the only way out of it.
        const SEQUENCE_OVER = ["unenrolled", "completed", "finished", "ended", "stopped", "interrupted", "cancelled", "canceled"];
        const showUnenroll = !!onUnenroll && !SEQUENCE_OVER.includes((s?.status || "").toLowerCase()) && !SEQUENCE_OVER.includes(key);
        return (
          <div
            key={s.sequence_id}
            className={`${styles.card} ${theme.card}`}
          >
            <div className={styles.headerRow}>
              <span className={`${styles.dot} ${theme.dot}`} />
              <span className={`${styles.headerLabel} ${theme.label}`}>
                {headerLabelForState(key)}
              </span>
            </div>
            <div className={styles.nameStatic}>{s.sequence_name || "-"}</div>
            <div className={styles.summary}>
              {/*
                Backend now ships the same `step_summary` string the marketing
                panel uses ("Step 2 of 4 — Email scheduled"), derived from the
                scheduled-mail roster. `sent_count` alone was wrong: it counts
                email sends only and stays 0 until the first mail actually goes
                out, which is why this read "Step 0 of 4" all through a live
                sequence. Dashes become commas to match the campaign card.
              */}
              {s?.step_summary
                ? String(s.step_summary).replace(/\s*[-—–]\s*/g, ", ")
                : `Step ${s?.current_step ?? s?.sent_count ?? 0} of ${s?.total_steps ?? "-"}`}
            </div>
            {showUnenroll && (
              <div className={styles.actions}>
                <Button
                  label="Unenroll"
                  size="sm36"
                  variant="ghost"
                  onClick={() => onUnenroll(s)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ActiveSequencePanel;
